import { PlanningsDeliveryWithLegRunEntity } from 'src/entities/PlanningsDelivery.entity';

/** useInfiniteQuery のキャッシュ形（useConvenienceDispatchDeliveries.query.ts:66-74） */
type InfinitePage = { data: readonly PlanningsDeliveryWithLegRunEntity[] };
type InfiniteCache = { pages: readonly InfinitePage[] };

/**
 * deliveries キャッシュは 2 つのキーで形が違う。
 * - CONVENIENCE_DISPATCH_DELIVERIES_QUERY_KEY は useInfiniteQuery → { pages, pageParams }
 * - CONVENIENCE_DISPATCH_ALL_DELIVERIES_QUERY_KEY は useQuery → 素の配列
 */
export type DeliveriesCache = readonly PlanningsDeliveryWithLegRunEntity[] | InfiniteCache;

const isInfiniteCache = (cache: DeliveriesCache): cache is InfiniteCache =>
  !Array.isArray(cache) && Array.isArray((cache as InfiniteCache).pages);

/** 配列 1 本ぶんの差し替え。変化が無ければ同じ参照を返す */
function replaceInList(
  list: readonly PlanningsDeliveryWithLegRunEntity[],
  targets: ReadonlySet<number>,
  fixed: boolean,
): readonly PlanningsDeliveryWithLegRunEntity[] {
  let changed = false;
  const next = list.map((d) => {
    // shift_truck_id = delivery.id
    if (!targets.has(d.id) || d.fixed === fixed) return d;
    changed = true;
    return { ...d, fixed };
  });
  return changed ? next : list;
}

/**
 * deliveries キャッシュの fixed を差し替えた値を返す。
 *
 * 確定／解除は API を待つと反映まで 9〜16 秒かかる（P1 実測）。その間ボタンを押しても
 * 🔒確定 バッジが変わらず「効いていない」ように見えるため、先にキャッシュを書き換える。
 * サーバが同じ値を返せば見た目は変わらない（車両/車番/ドライバーの楽観表示と同じ考え方）。
 *
 * Why not 破壊的更新: React Query は参照で変化を見るので、元を書き換えると再描画されない。
 * 変化が無いときに新しい参照を作らないのも同じ理由（無関係な再描画を避ける）。
 */
export function applyFixedToDeliveries(
  deliveries: undefined, shiftTruckIds: readonly number[], fixed: boolean,
): undefined;
export function applyFixedToDeliveries<T extends DeliveriesCache>(
  deliveries: T, shiftTruckIds: readonly number[], fixed: boolean,
): T;
// 実装シグネチャ。戻り値は入力と同じ形（配列 or { pages }）で、呼び出し側は
// 形を意識せず setQueryData へ渡せる
export function applyFixedToDeliveries(
  deliveries: DeliveriesCache | undefined,
  shiftTruckIds: readonly number[],
  fixed: boolean,
): DeliveriesCache | undefined {
  if (!deliveries) return deliveries;
  const targets = new Set(shiftTruckIds);

  if (isInfiniteCache(deliveries)) {
    let changed = false;
    const pages = deliveries.pages.map((page) => {
      const data = replaceInList(page.data, targets, fixed);
      if (data === page.data) return page;
      changed = true;
      return { ...page, data };
    });
    return changed ? { ...deliveries, pages } : deliveries;
  }

  return replaceInList(deliveries, targets, fixed);
}
